/** Recover only a small field's automatic iOS focus zoom. */
export interface FocusZoomHost {
  scale(): number;
  viewport(): string | null;
  setViewport(value: string): void;
  editableActive(): boolean;
  now(): number;
  later(callback: () => void, delay: number): ReturnType<typeof setTimeout>;
  cancel(timer: ReturnType<typeof setTimeout>): void;
}
export function createFocusZoomRecovery(host: FocusZoomHost) {
  let session: { original: string; started: number; expected: number; owned: boolean } | undefined;
  let temporary: string | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const stopTimer = () => { if (timer !== undefined) host.cancel(timer); timer = undefined; };
  const restore = () => {
    stopTimer();
    if (temporary && session && host.viewport() === temporary) host.setViewport(session.original);
    temporary = undefined;
    session = undefined;
  };
  return {
    focus(fontSize: number) {
      if (temporary) restore();
      if (session) return; // A field-to-field transfer belongs to the same interaction.
      const original = host.viewport();
      if (original === null || Math.abs(host.scale() - 1) > 0.01 || fontSize >= 16 || fontSize <= 0) return;
      session = { original, started: host.now(), expected: 16 / fontSize, owned: false };
    },
    resized() {
      if (!session || temporary) return;
      if (host.viewport() !== session.original) { restore(); return; }
      const scale = host.scale();
      if (scale <= 1.05) return;
      if (!session.owned && host.now() - session.started > 1500) { restore(); return; }
      if (Math.abs(scale - session.expected) > 0.12) { if (session.owned) restore(); return; }
      session.owned = true;
    },
    blur() {
      stopTimer();
      timer = host.later(() => {
        timer = undefined;
        if (host.editableActive()) return;
        if (!session?.owned || host.viewport() !== session.original || host.scale() <= 1.05) { restore(); return; }
        // This is a one-shot reset after editing, never a persistent zoom policy.
        temporary = session.original + ", maximum-scale=1";
        host.setViewport(temporary);
        timer = host.later(restore, 350);
      }, 0);
    },
    cancel: restore,
  };
}
export function installFocusZoomRecovery() {
  if (!/iPhone|iPad|iPod/.test(navigator.userAgent) || !window.visualViewport) return () => {};
  const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  if (!meta) return () => {};
  const editable = (target: EventTarget | null): target is HTMLElement => target instanceof HTMLElement && target.matches('input, textarea, select, [contenteditable="true"]');
  const recovery = createFocusZoomRecovery({
    scale: () => window.visualViewport!.scale,
    viewport: () => meta.getAttribute("content"),
    setViewport: value => meta.setAttribute("content", value),
    editableActive: () => editable(document.activeElement),
    now: () => performance.now(),
    later: (callback, delay) => window.setTimeout(callback, delay),
    cancel: timer => window.clearTimeout(timer),
  });
  const focus = (event: FocusEvent) => {
    if (editable(event.target)) recovery.focus(parseFloat(getComputedStyle(event.target).fontSize));
  };
  const touch = (event: TouchEvent) => { if (event.touches.length > 1) recovery.cancel(); };
  const visibility = () => { if (document.hidden) recovery.cancel(); };
  document.addEventListener("focusin", focus);
  document.addEventListener("focusout", recovery.blur);
  document.addEventListener("touchstart", touch, { capture: true, passive: true });
  document.addEventListener("gesturestart", recovery.cancel, true);
  document.addEventListener("dblclick", recovery.cancel, true);
  document.addEventListener("visibilitychange", visibility);
  window.addEventListener("orientationchange", recovery.cancel);
  window.visualViewport.addEventListener("resize", recovery.resized);
  return () => {
    recovery.cancel();
    document.removeEventListener("focusin", focus);
    document.removeEventListener("focusout", recovery.blur);
    document.removeEventListener("touchstart", touch, true);
    document.removeEventListener("gesturestart", recovery.cancel, true);
    document.removeEventListener("dblclick", recovery.cancel, true);
    document.removeEventListener("visibilitychange", visibility);
    window.removeEventListener("orientationchange", recovery.cancel);
    window.visualViewport?.removeEventListener("resize", recovery.resized);
  };
}
